import { OrderViewModel } from "@affinity/Store/MyAccountState";
import ServiceAccounts from "@affinity/Store/ServiceAccounts";
import { customDispatch } from "@affinity/Store/ServiceBase";
// import ServiceTracking from "@affinity/Store/ServiceTracking";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { OrderCollectionModel, ShipToCollectionModel } from "@insite/client-framework/Types/ApiModels";

// External properties to call chain
interface LoadMyAccountOrdersParameter {
    pageSize: number,
    billToId: string,
}

// Internal chain use
interface Props {
    rawOrders: OrderCollectionModel;
    rawAddresses: ShipToCollectionModel;
    mappedOrders: OrderViewModel[];
    completedOrders: OrderViewModel[];
    totalCompletedOrders: number;
    openOrders: OrderViewModel[];
    totalOpenOrders: number;
    error?: unknown;
}

type HandlerType = Handler<LoadMyAccountOrdersParameter, Props>;

export const StartLoadOrders: HandlerType = props => {
    (props.dispatch as customDispatch)({
        type: "MyAccount/Loading",
    })
}

export const LoadOrders: HandlerType = async props => {
    try {
        const service = new ServiceAccounts();
        props.rawOrders = await service.getOrderHistoryAll();
    } catch (error) {
        logger.warn(`Orders Load Step - Failed to load orders: ${error}`);
        props.error = error;
    }
};

export const MapToViewModel: HandlerType = props => {
    const tempOrders: OrderViewModel[] = [];
    const service = new ServiceAccounts();
    props.rawOrders?.orders?.forEach(oDV => {
        oDV?.orderLines?.forEach(oDL => {
                if (oDV.erpOrderNumber) {
                const shipTo = oDV.shipCode;
                const deliveryDue = service.getHarrierPricingInfo(oDL)?.harrierSource?.deliveryBy;
                tempOrders.push({
                    id: oDV.erpOrderNumber,
                    orderLineId: oDL.id,
                    name: oDL.description,
                    address: shipTo, // NEED TO GET SHIP TO MAPPED (CHILD OF CURRENT ACCONT OR OTHER ACCOUNT)
                    amount: oDL.qtyOrdered,
                    deliveryDue: deliveryDue!, // NEED TO EXTRACT FROM CUSTOM PROPERTY
                    status: oDV.status === "Closed" ? "Completed" : "Open", // TODO: Clarify if Line Status is being updated by order synch
                });
            }
        })
    });
    props.mappedOrders = tempOrders;
}

export const LoadAddresses: HandlerType = async props => {
    try {
        const service = new ServiceAccounts();
        if (props.parameter.billToId) {
            props.rawAddresses = await service.getAddresses(props.parameter.billToId);
        }
    } catch (error) {
        logger.warn(`Orders Load Step - Failed to load addresses: ${error}`);
        props.error = error;
    }
}

export const MergeAddresses: HandlerType = props => {
    props.mappedOrders.forEach(o => {
        const objectAddress = o.address != null ? o.address : "";
        const address = props.rawAddresses.shipTos?.find(a => a.customerSequence === objectAddress.toString());
        // TODO: Update address parameters if needed
        // Only works if user has access to address via login
        if (address) {
            o.address = `${address?.address1}, ${address?.postalCode}`;
        } else {
            o.address = '';
        }
    })
}

export const SplitOrders: HandlerType = props => {
    props.openOrders = [];
    props.completedOrders = [];

    // TODO: Redo to make 2 api calls with rule to split to get total via API
    // TODO: Potentially redo API to get rid of pagination and preprocess data server side
    // TODO: Clarify status of open / completed

    props.mappedOrders.forEach(o => {
        if (o.status === "Completed") {
            props.completedOrders.push(o);
        } else {
            props.openOrders.push(o);
        }
    })

    props.totalOpenOrders = props.openOrders.length; // Temp solution, likely need a new API with SQL check
    props.totalCompletedOrders = props.completedOrders.length // Temp solution, likely need a new API with SQL check
}

export const UpdateState: HandlerType = props => {
    (props.dispatch as customDispatch)({
        type: "MyAccount/Loaded",
        openOrders: props.openOrders,
        totalOpenOrders: props.totalOpenOrders,
        completedOrders: props.completedOrders,
        totalCompletedOrders: props.totalCompletedOrders,
    });
};

// NOT USED
// export const Tracking: HandlerType = props => {
//     new ServiceTracking().trackLoadMyAccountOrders();
// };

export const chain = [
    StartLoadOrders,
    LoadOrders,
    MapToViewModel,
    LoadAddresses,
    MergeAddresses,
    SplitOrders,
    UpdateState,
    // Tracking,
];

const loadMyAccountOrders = createHandlerChainRunner(chain, "LoadMyAccountOrders");
export default loadMyAccountOrders;